import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchOne as fetchCategoryConfig,
  saveOne as saveCategoryConfig
} from 'app/api/admin/browse/categoryConfigsApi';
import ConfirmUnsavedChangesModal from 'app/components/admin/browse/shared/ConfirmUnsavedChangesModal';
import FatalErrorPage from 'app/components/admin/browse/shared/FatalErrorPage';
import ErrorPage from 'app/components/ErrorPage';
import { MESSAGE_ACTION_GENERIC_ERROR, MESSAGE_ACTION_GENERIC_SUCCESS } from 'app/constants/admin/browse';
import { TOP_LEVEL_CATEGORY_ID, TOP_LEVEL_CATEGORY_TITLE_BY_ID } from 'app/constants/AdminBrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import {
  hasAdminPermissions,
  isTopLevelCategoryId,
  makeCategorySlug,
  makeEmptyCategoryConfig
} from 'app/helpers/admin/browse/categories';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseCategoriesEditPage from './AdminBrowseCategoriesEditPage';

const AdminBrowseCategoriesEditPageContainer = ({
  basePageRoute,
  categoryConfigId,
  localeCode,
  topLevelCategoryId
}) => {
  const apiClient = useApiClient();
  const dispatch = useDispatch();
  const { isAvailableLocaleCode, mapLocaleCodeToLabel } = useLocaleCodes();

  const [state, setState] = useState({
    categoryConfig: null,
    categoryConfigWorkingCopy: null,
    hasFatalServerError: false,
    hasUnsavedChanges: false,
    isFetching: true
  });
  const { categoryConfig, categoryConfigWorkingCopy, hasFatalServerError, hasUnsavedChanges, isFetching } = state;

  const currentUser = useSelector(selectCurrentUser);
  const currentUsername = currentUser?.get('name');

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (categoryConfigId) {
          return fetchCategoryConfig(apiClient, topLevelCategoryId, localeCode, categoryConfigId);
        }

        return makeEmptyCategoryConfig(subdomain, localeCode, topLevelCategoryId);
      })
      .then(fetchedCategoryConfig => {
        const newCategoryConfigWorkingCopy = cloneDeep(fetchedCategoryConfig);

        setState(prevState => {
          return {
            ...prevState,
            categoryConfig: fetchedCategoryConfig,
            categoryConfigWorkingCopy: newCategoryConfigWorkingCopy
          };
        });
      })
      .catch(() => {
        setState(prevState => ({ ...prevState, hasFatalServerError: true }));
      })
      .finally(() => {
        setState(prevState => ({ ...prevState, isFetching: false }));
      });
  }, [apiClient, categoryConfigId, currentUsername, dispatch, localeCode, subdomain, topLevelCategoryId]);

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const isValidLocaleCode = isAvailableLocaleCode(localeCode);
  if (!isValidLocaleCode) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const isValidTopLevelCategoryId = isTopLevelCategoryId(topLevelCategoryId);
  if (!isValidTopLevelCategoryId) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const backLinkLabel = 'Back to categories';
  const backLinkUrl = `${basePageRoute}/${topLevelCategoryId}`;
  const heading = `${TOP_LEVEL_CATEGORY_TITLE_BY_ID[topLevelCategoryId]} (${mapLocaleCodeToLabel(localeCode)})`;

  if (hasFatalServerError) {
    return <FatalErrorPage backLinkLabel={backLinkLabel} backLinkUrl={backLinkUrl} heading={heading} />;
  }

  const handleWorkingCopyChange = newCategoryConfigWorkingCopy => {
    const newHasUnsavedChanges = !isEqual(newCategoryConfigWorkingCopy, categoryConfig);

    setState(prevState => {
      return {
        ...prevState,
        categoryConfigWorkingCopy: newCategoryConfigWorkingCopy,
        hasUnsavedChanges: newHasUnsavedChanges
      };
    });
  };

  const handleSaveCategoryConfig = () => {
    setState(prevState => ({ ...prevState, isFetching: true }));

    const categoryConfigToSave = cloneDeep(categoryConfigWorkingCopy);

    categoryConfigToSave.updatedAt = Date.now();
    categoryConfigToSave.updatedBy = currentUsername;

    // For now, we are auto-generating the category slugs.
    // We will make this editable by the user in the near future.
    Object.values(categoryConfigToSave.categoryById).forEach(category => {
      category.slug = makeCategorySlug(category.title);
    });

    saveCategoryConfig(apiClient, topLevelCategoryId, localeCode, categoryConfigToSave)
      .then(savedCategoryConfig => {
        setState(prevState => {
          return {
            ...prevState,
            categoryConfig: savedCategoryConfig,
            categoryConfigWorkingCopy: cloneDeep(savedCategoryConfig),
            hasUnsavedChanges: false
          };
        });

        dispatch(MESSAGE_ACTION_GENERIC_SUCCESS);
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);

        /* eslint-disable-next-line no-console */
        console.error('Failed to save category config', err);
      })
      .finally(() => {
        setState(prevState => ({ ...prevState, isFetching: false }));
      });
  };

  return (
    <>
      <ConfirmUnsavedChangesModal hasUnsavedChanges={hasUnsavedChanges} />
      <AdminBrowseCategoriesEditPage
        backLinkLabel={backLinkLabel}
        backLinkUrl={backLinkUrl}
        categoryConfig={categoryConfigWorkingCopy}
        hasUnsavedChanges={hasUnsavedChanges}
        heading={heading}
        isFetching={isFetching}
        onChange={handleWorkingCopyChange}
        onSave={handleSaveCategoryConfig}
        topLevelCategoryId={topLevelCategoryId}
      />
    </>
  );
};

AdminBrowseCategoriesEditPageContainer.propTypes = {
  basePageRoute: PropTypes.string.isRequired,
  categoryConfigId: PropTypes.string,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired,
  topLevelCategoryId: PropTypes.oneOf(Object.values(TOP_LEVEL_CATEGORY_ID)).isRequired
};

export default AdminBrowseCategoriesEditPageContainer;
